import React, { createContext, useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  addExternalPropertiesFromAPI,
  fetchExternalPropertiesFromAPI,
  fetchMapProperties,
  fetchPropertiesFromAPI,
  fetchPropertyDetails,
  removeExternalPropertyFromAPI,
  fetchDoubtPropertiesFromAPI,
  fetchDiscardedPropertiesFromAPI,
  addDoubtPropertyToAPI,
  removeDoubtPropertyFromAPI,
  addDiscardedPropertyToAPI,
  removeDiscardedPropertyFromAPI,
  fetchTopGrossYieldProperty,
  fetchLowestPricePerSquareMeterProperty,
  fetchMarketStatistics,
  fetchNearbyMarketStatistics,
  saveCustomExternalPropertyAPI,
  updateExternalProperty,
} from "api/property";
import { useOrganiseList } from "./OrganiseListContext";
import useProperties from "hooks/useProperties";

export const PropertyContext = createContext();

export const PropertyProvider = ({ children }) => {
  const [properties, setProperties] = useState([]);
  const [mapProperties, setMapProperties] = useState([]);
  const [propertyDetails, setPropertyDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [loadingMapProperty, setLoadingMapProperty] = useState(false);
  const [error, setError] = useState(null);
  const [externalProperties, setExternalProperties] = useState([]);
  const [isLoadingExternal, setIsLoadingExternal] = useState(false);
  const [isAddingExternal, setIsAddingExternal] = useState(false);
  const [errorExternal, setErrorExternal] = useState(null);
  const { selectedOrganiseList } = useOrganiseList();
  const organizerListId = selectedOrganiseList?._id;
  const propertyCache = useRef(new Map());
  const mapScrollRef = useRef(0);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [isMapPopupVisible, setIsMapPopupVisible] = useState(null);
  const [selectedPropertyPopup, setSelectedPropertyPopup] = useState({
    property: null,
    id: null,
  });
  const [doubtProperties, setDoubtProperties] = useState([]);
  const [doubtPropertyIds, setDoubtPropertyIds] = useState([]);
  const [discardedProperties, setDiscardedProperties] = useState([]);
  const [discardedPropertyIds, setDiscardedPropertyIds] = useState([]);
  const [isLoadingDoubt, setIsLoadingDoubt] = useState(false);
  const [isLoadingDiscarded, setIsLoadingDiscarded] = useState(false);
  const [errorDoubt, setErrorDoubt] = useState(null);
  const [errorDiscarded, setErrorDiscarded] = useState(null);
  const [topGrossYieldProperties, setTopGrossYieldProperties] = useState([]);
  const [isFetchingTopGrossYieldProperty, setIsFetchingTopGrossYieldProperty] =
    useState(false);
  const [isFetchingLowestPricePerSqm, setIsFetchingLowestPricePerSqm] =
    useState(false);
  const [lowestPricePerSqmProperties, setLowestPricePerSqmProperties] =
    useState([]);
  const [isFetchingMarketStatistics, setIsFetchingMarketStatistics] =
    useState(false);
  const [
    dashboardMarketStatisticsProperties,
    setDashboardMarketStatisticsProperties,
  ] = useState([]);
  const [
    nearbyMarketStatisticsProperties,
    setNearbyMarketStatisticsProperties,
  ] = useState([]);
  const [
    isFetchingNearbyMarketStatistics,
    setIsFetchingNearbyMarketStatistics,
  ] = useState(false);
  const [openLocationIntelligencePopup, setOpenLocationIntelligencePopup] =
    useState(null);
  const [isPaginatedPropertyPopupOpen, setIsPaginatedPropertyPopupOpen] =
    useState(null);
  const [activeMapProperty, setActiveMapProperty] = useState(null);

  const handleMapToggle = async (coordinates) => {
    if (isMapPopupVisible) {
      setIsMapPopupVisible(null);
      await new Promise((resolve) => setTimeout(resolve, 0));
      window.scrollTo({
        top: mapScrollRef.current,
        left: 0,
        behavior: "smooth",
      });
    } else {
      mapScrollRef.current = window.scrollY;
      setIsMapPopupVisible(coordinates);
    }
  };

  const handleMapPopupToggle = async (coordinates) => {
    setIsMapPopupVisible(coordinates);
  };

  const activatePropertyOnMap = (propertyId) => {
    setActiveMapProperty(propertyId);
  };

  const toggleLocationItelligencePopup = (id) => {
    setOpenLocationIntelligencePopup((prevId) => (prevId === id ? null : id));
  };

  const togglePaginatedPropertyPopup = (id) => {
    setIsPaginatedPropertyPopupOpen((prevId) => (prevId === id ? null : id));
  };

  // Search properties in search page
  const searchProperties = async (filters, nextUrl = null) => {
    if (nextUrl) {
      setPaginationLoading(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await fetchPropertiesFromAPI(filters, nextUrl);
      if (nextUrl) {
        setProperties((prevProperties) => ({
          ...result,
          results: [...prevProperties.results, ...result.results],
        }));
      } else {
        setProperties(result);
      }
      return result;
    } catch (err) {
      setError(err.message);
    } finally {
      if (nextUrl) {
        setPaginationLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Search properties in map page
  const searchMapProperties = async (filters) => {
    setError(null);
    try {
      const result = await fetchMapProperties(filters);
      setMapProperties(result);
      return result;
    } catch (err) {
      setError(err.message);
    }
  };

  // Add external property
  const addExternalProperty = async (externalUrl) => {
    setIsAddingExternal(true);
    try {
      await addExternalPropertiesFromAPI(organizerListId, externalUrl);
      toast.success(
        "Abbiamo recuperato le informazioni dell'annuncio correttamente!"
      );
      await fetchExternalProperties();
    } catch (err) {
      throw err;
    } finally {
      setIsAddingExternal(false);
    }
  };

  // Add external property
  const saveCustomExternalProperty = async (
    propertyTitle,
    propertyPrice,
    propertyImage,
    externalUrl
  ) => {
    setIsAddingExternal(true);
    try {
      await saveCustomExternalPropertyAPI(
        organizerListId,
        propertyTitle,
        propertyPrice,
        propertyImage,
        externalUrl
      );
      toast.success(
        `Proprietà esterna aggiunta correttamente alla ${selectedOrganiseList?.name}`
      );
      await fetchExternalProperties();
    } catch (err) {
      toast.error(err.message || "An error occurred");
    } finally {
      setIsAddingExternal(false);
    }
  };

  // External Properties
  const fetchExternalProperties = async (nextUrl = null) => {
    setIsLoadingExternal(true);
    setErrorExternal(null);
    try {
      const result = await fetchExternalPropertiesFromAPI(
        organizerListId,
        nextUrl
      );
      if (nextUrl) {
        setExternalProperties((prevProperties) => ({
          ...result,
          results: [...prevProperties.results, ...result.results],
        }));
      } else {
        setExternalProperties(result);
      }
      return result;
    } catch (err) {
      console.error("Error while fetching external properties", err);
      setErrorExternal(err.message);
    } finally {
      setIsLoadingExternal(false);
    }
  };

  // Remove External Properties
  const removeExternalProperties = async (propertyId) => {
    setIsLoadingExternal(true);
    setErrorExternal(null);
    try {
      const result = await removeExternalPropertyFromAPI(
        organizerListId,
        propertyId
      );

      // Update the state
      if (result.success) {
        setExternalProperties((prevProperties) => ({
          ...prevProperties,
          results: prevProperties.results.filter((property) => {
            console.log("property", property);
            if (typeof property.propertyId === "string") {
              return property.propertyId !== propertyId;
            } else if (property.propertyId?._id) {
              return property.propertyId._id !== propertyId;
            }
            return true;
          }),
        }));
      }
      toast.success("Proprietà rimossa dalla lista correttamente");
      return result;
    } catch (err) {
      console.error("Error while removing external property", err);
      toast.error(err.message || "Error in removing property");
    } finally {
      setIsLoadingExternal(false);
    }
  };

  // Update the bookmark note
  const updateExternalPropertyNote = async (propertyId, note) => {
    setIsAddingExternal(true);
    try {
      await updateExternalProperty(propertyId, organizerListId, note);
      toast.success("Property note updated successfully.");
    } catch (err) {
      toast.error(err.message || "An error occurred");
    } finally {
      setIsAddingExternal(false);
    }
  };

  const getPropertyDetails = async (propertyId) => {
    getNearbyMarketStatistics(propertyId);

    // Check if the details are already cached
    if (propertyCache.current.has(propertyId)) {
      setPropertyDetails(propertyCache.current.get(propertyId));
      return propertyCache.current.get(propertyId);
    }

    setLoadingMapProperty(true);
    setError(null);
    try {
      const result = await fetchPropertyDetails(propertyId);

      // Cache the result
      propertyCache.current.set(propertyId, result);
      setPropertyDetails(result);
      return result;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMapProperty(false);
    }
  };

  // Fetch doubt properties
  const fetchDoubtProperties = async (
    isPopulateProperty = false,
    nextUrl = null
  ) => {
    setIsLoadingDoubt(true);
    setErrorDoubt(null);
    try {
      const fetchedDoubtProperties = await fetchDoubtPropertiesFromAPI(
        isPopulateProperty,
        nextUrl
      );

      // Extract property IDs
      const ids =
        fetchedDoubtProperties?.results?.map((prop) =>
          typeof prop.propertyId === "string"
            ? prop.propertyId
            : prop.propertyId._id
        ) || [];

      if (isPopulateProperty) {
        if (nextUrl) {
          setDoubtProperties((prevVal) => ({
            ...fetchedDoubtProperties,
            results: [...prevVal.results, ...fetchedDoubtProperties.results],
          }));
        } else {
          setDoubtProperties(fetchedDoubtProperties);
        }
        setDoubtPropertyIds((prevIds) => [
          ...prevIds,
          ...ids.filter((id) => !prevIds.includes(id)),
        ]);
      } else {
        setDoubtPropertyIds(ids);
      }
    } catch (err) {
      console.error("Error fetching doubt properties", err);
      setErrorDoubt(err.message);
    } finally {
      setIsLoadingDoubt(false);
    }
  };

  // Add doubt property
  const addDoubtProperty = async (propertyId, averageGrossYield) => {
    setIsLoadingDoubt(true);
    try {
      await addDoubtPropertyToAPI(propertyId, averageGrossYield);
      toast.success("Proprietà aggiunta correttamente alla lista dubbi");
    } catch (err) {
      toast.error(
        err.message || "An error occurred while adding doubt property"
      );
      throw new Error(err.message);
    } finally {
      setIsLoadingDoubt(false);
    }
  };

  // Remove doubt property
  const removeDoubtProperty = async (propertyId) => {
    setIsLoadingDoubt(true);
    try {
      await removeDoubtPropertyFromAPI(propertyId);

      if (doubtProperties?.results && doubtProperties?.results?.length > 0) {
        // Update the state to remove the property
        setDoubtProperties((prevProperties) => {
          // Filter out the property to remove
          const updatedResults = prevProperties.results.filter(
            (property) => property.propertyId._id !== propertyId
          );

          // Return the updated state
          return {
            ...prevProperties,
            results: updatedResults,
          };
        });
      }
    } catch (err) {
      toast.error(err.message || "Error while removing doubt property");
      throw new Error(err.message);
    } finally {
      setIsLoadingDoubt(false);
    }
  };

  // Fetch discarded properties
  const fetchDiscardedProperties = async (
    isPopulateProperty = false,
    nextUrl = null
  ) => {
    setIsLoadingDiscarded(true);
    setErrorDiscarded(null);
    try {
      const fetchedDiscardedProperties = await fetchDiscardedPropertiesFromAPI(
        isPopulateProperty,
        nextUrl
      );
      const { results = [] } = fetchedDiscardedProperties; // Destructure with default value

      // Extract property IDs
      const ids =
        results.map((prop) =>
          typeof prop.propertyId === "string"
            ? prop.propertyId
            : prop.propertyId._id
        ) || [];

      if (isPopulateProperty) {
        if (nextUrl) {
          setDiscardedProperties((prevVal) => ({
            ...fetchedDiscardedProperties,
            results: [...prevVal.results, ...results],
          }));
        } else {
          setDiscardedProperties(fetchedDiscardedProperties);
        }
        setDiscardedPropertyIds((prevIds) => [
          ...prevIds,
          ...ids.filter((id) => !prevIds.includes(id)),
        ]);
      } else {
        setDiscardedPropertyIds(ids);
      }
    } catch (err) {
      console.error("Error fetching discarded properties", err);
      setErrorDiscarded(err.message);
    } finally {
      setIsLoadingDiscarded(false);
    }
  };

  // Add discarded property
  const addDiscardedProperty = async (propertyId, averageGrossYield) => {
    setIsLoadingDiscarded(true);
    try {
      await addDiscardedPropertyToAPI(propertyId, averageGrossYield);
      toast.success(
        "Proprietà aggiunta correttamente alla lista delle proprietà scartate"
      );
    } catch (err) {
      toast.error(
        err.message || "An error occurred while adding discarded property"
      );
      throw new Error(err.message);
    } finally {
      setIsLoadingDiscarded(false);
    }
  };

  // Remove discarded property
  const removeDiscardedProperty = async (propertyId) => {
    setIsLoadingDiscarded(true);
    try {
      await removeDiscardedPropertyFromAPI(propertyId);

      if (
        discardedProperties?.results &&
        discardedProperties?.results?.length > 0
      ) {
        // Update the state to remove the property
        setDiscardedProperties((prevProperties) => {
          // Filter out the property to remove
          const updatedResults = prevProperties.results.filter(
            (property) => property.propertyId._id !== propertyId
          );

          // Return the updated state
          return {
            ...prevProperties,
            results: updatedResults,
          };
        });
      }
    } catch (err) {
      toast.error(err.message || "Error while removing discarded property");
      throw new Error(err.message);
    } finally {
      setIsLoadingDiscarded(false);
    }
  };

  // Fetch lowest price per square meter property
  const getLowestPricePerSquareMeterProperty = async () => {
    setIsFetchingLowestPricePerSqm(true);
    try {
      const results = await fetchLowestPricePerSquareMeterProperty();

      setLowestPricePerSqmProperties(results);
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsFetchingLowestPricePerSqm(false);
    }
  };

  // Fetch top gross yield property
  const getTopGrossYieldProperty = async () => {
    setIsFetchingTopGrossYieldProperty(true);
    try {
      const results = await fetchTopGrossYieldProperty();

      setTopGrossYieldProperties(results);
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsFetchingTopGrossYieldProperty(false);
    }
  };

  // Fetch properties for dashboard charts
  const getDashboardMarketStatistics = async (filters) => {
    setIsFetchingMarketStatistics(true);
    try {
      const result = await fetchMarketStatistics(filters);
      setDashboardMarketStatisticsProperties(result);

      return result;
    } catch (err) {
      console.log(err.message);
    } finally {
      setIsFetchingMarketStatistics(false);
    }
  };

  // Fetch nearby market statistics for property details
  const getNearbyMarketStatistics = async (propertyId) => {
    setIsFetchingNearbyMarketStatistics(true);
    try {
      const result = await fetchNearbyMarketStatistics(propertyId);
      setNearbyMarketStatisticsProperties(result);
      return result;
    } catch (err) {
      console.log(err.message);
    } finally {
      setIsFetchingNearbyMarketStatistics(false);
    }
  };

  return (
    <PropertyContext.Provider
      value={{
        properties,
        setProperties,
        loading,
        paginationLoading,
        error,
        searchProperties,
        externalProperties,
        isLoadingExternal,
        isAddingExternal,
        errorExternal,
        addExternalProperty,
        saveCustomExternalProperty,
        fetchExternalProperties,
        removeExternalProperties,
        searchMapProperties,
        mapProperties,
        propertyDetails,
        getPropertyDetails,
        loadingMapProperty,
        isMapVisible,
        setIsMapVisible,
        isMapPopupVisible,
        handleMapToggle,
        handleMapPopupToggle,
        selectedPropertyPopup,
        setSelectedPropertyPopup,
        doubtProperties,
        doubtPropertyIds,
        discardedProperties,
        discardedPropertyIds,
        isLoadingDoubt,
        isLoadingDiscarded,
        fetchDoubtProperties,
        addDoubtProperty,
        removeDoubtProperty,
        fetchDiscardedProperties,
        addDiscardedProperty,
        removeDiscardedProperty,
        errorDoubt,
        errorDiscarded,
        getTopGrossYieldProperty,
        topGrossYieldProperties,
        isFetchingTopGrossYieldProperty,
        getLowestPricePerSquareMeterProperty,
        isFetchingLowestPricePerSqm,
        lowestPricePerSqmProperties,
        getDashboardMarketStatistics,
        isFetchingMarketStatistics,
        dashboardMarketStatisticsProperties,
        nearbyMarketStatisticsProperties,
        isFetchingNearbyMarketStatistics,
        openLocationIntelligencePopup,
        toggleLocationItelligencePopup,
        isPaginatedPropertyPopupOpen,
        togglePaginatedPropertyPopup,
        updateExternalPropertyNote,
        activeMapProperty,
        activatePropertyOnMap,
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
};
